import type { ComparisonPage } from '../types';

export const shopifyAnalyticsVsWarehouse: ComparisonPage = {
  slug: 'shopify-analytics-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'shopify-analytics',
  title: 'Shopify Analytics vs a custom data warehouse: when built-in reports stop being enough',
  metaTitle: 'Shopify Analytics vs a Custom Data Warehouse (2026): When to Upgrade',
  description:
    'Shopify’s built-in analytics are free, real time, and better than ever. Here is what they cover and the point where brands need a warehouse instead.',
  keywords: ['shopify analytics vs', 'shopify reports limitations', 'shopifyql', 'shopify data warehouse', 'when to build an ecommerce data warehouse'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'Start with Shopify Analytics. It is included in every plan, runs in real time, has cohorts, profit reports from your unit costs, ShopifyQL, and an AI assistant that builds reports from questions. Brands need a warehouse when important revenue happens outside the Shopify store, when profit has to include fees and ad spend, or when finance needs numbers that tie to the books.',
  differences: [
    { heading: 'Store data vs business data', body: 'Shopify reports read the store’s own database. A warehouse adds Amazon fees, TikTok Shop, wholesale, ad spend, and costs.' },
    { heading: 'Gross profit vs contribution margin', body: 'Shopify’s profit reports use the cost per item you enter; fees and ad spend are not included. A warehouse models the full margin.' },
    { heading: 'Exports vs a warehouse', body: 'Shopify exports reports as files. A warehouse keeps all history in Snowflake for any tool to query.' },
  ],
  sections: [
    {
      heading: 'What Shopify Analytics does well',
      paragraphs: [
        'Shopify redesigned its analytics in 2025: real-time data, custom dashboards, explorations, benchmarks, and customer cohort analysis. ShopifyQL lets you query store data, and Sidekick, Shopify’s AI assistant, writes that query from a plain-English question. Advanced and Plus plans add more reports, and Plus adds cross-store dashboards.',
        'For a brand that sells almost entirely through its Shopify store, this covers most daily questions for no extra cost.',
      ],
    },
    {
      heading: 'Where it stops',
      paragraphs: [
        'Amazon orders synced through Shopify’s marketplace app appear as orders, but we found no reporting on Amazon fees or settlements. We found no TikTok Shop, ERP, 3PL, or wholesale-platform reporting in the analytics docs. ShopifyQL is not standard SQL and covers store data only, and there is no warehouse sync.',
        'Those gaps are the usual triggers: [Shopify, finance, and marketing reporting different revenue](/blog/why-shopify-finance-marketing-report-different-revenue), [refunds dated differently from orders](/blog/shopify-refund-date-vs-order-date), and [Amazon profit changing after the sale](/blog/why-amazon-profit-changes-after-sale).',
      ],
    },
  ],
  pickThem: [
    'Nearly all of your revenue goes through your Shopify store.',
    'Gross profit from unit cost is enough for your decisions.',
    'You want real-time reports at no extra cost.',
  ],
  pickUs: [
    'Amazon, TikTok Shop, wholesale, or retail are a real share of revenue.',
    'You need margin after fees, shipping, refunds, and ad spend.',
    'Finance needs numbers that reconcile to payouts and the books.',
  ],
  byTeam: [
    { team: 'Shopify-only brand', pick: 'theirs', why: 'Real-time, free, and improving every release.' },
    { team: 'Shopify Plus brand with several stores', pick: 'theirs', why: 'Cross-store dashboards are built into Plus.' },
    { team: 'Brand on Shopify, Amazon, and TikTok Shop', pick: 'ours', why: 'Every channel and its fees in one model.' },
    { team: 'Brand whose finance team reconciles revenue monthly', pick: 'ours', why: 'Revenue that ties to payouts and accounting.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace Shopify Analytics?',
      answer: 'No. Teams keep using Shopify’s reports for day-to-day store questions. A warehouse adds what Shopify cannot see: other channels, fees, ad spend, and costs.',
    },
    {
      question: 'Can ShopifyQL replace SQL in a warehouse?',
      answer: 'For store data, often. ShopifyQL is a commerce query language over Shopify’s own data. It cannot join Amazon, ad platform, or ERP data.',
    },
    {
      question: 'Which Shopify plans include analytics?',
      answer: 'Shopify says its main analytics features come with every plan. Higher plans add more reports, and Plus adds cross-store analytics.',
    },
  ],
  related: ['peel-vs-custom-data-warehouse', 'lifetimely-vs-custom-data-warehouse', 'triple-whale-vs-custom-data-warehouse'],
};
